import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, FileText, Archive, Eye, Lock, RefreshCw, Calendar, Filter
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../utils/helpers';
import Button from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const RecordRoom = () => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);

  useEffect(() => {
    fetchArchivedFiles();
  }, [page, search]);

  const fetchArchivedFiles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/efile/archived?page=${page}&limit=10&search=${search}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setFiles(response.data.data.files);
        setTotalPages(response.data.data.totalPages);
        setTotalFiles(response.data.data.total);
      }
    } catch (error) {
      console.error('Error fetching archived files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1); // Reset to page 1 on search
    fetchArchivedFiles();
  };

  return (
    <div className="p-6 w-full mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Archive className="h-6 w-6 text-red-700" />
            Digital Record Room
          </h1>
          <p className="text-gray-500 mt-1">Archived and closed files (Read-Only)</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search by File Number or Subject..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit">Search</Button>
          <Button type="button" variant="outline" onClick={() => { setSearch(''); setPage(1); }}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </form>
      </div>

      {/* Files List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 w-16">#</th>
                <th className="px-6 py-3 min-w-[300px]">File Number & Subject</th>
                <th className="px-6 py-3 whitespace-nowrap">Archived By</th>
                <th className="px-6 py-3 whitespace-nowrap">Archived Date</th>
                <th className="px-6 py-3 text-right whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : files.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500 flex flex-col items-center gap-2">
                    <Archive className="h-8 w-8 text-gray-300" />
                    No archived files found
                  </td>
                </tr>
              ) : (
                files.map((file, index) => (
                  <tr key={file.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-gray-500">
                      {(page - 1) * 10 + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 flex items-center gap-2">
                           <FileText className="h-4 w-4 text-gray-400" />
                           {file.fileNumber}
                        </span>
                        <span className="text-gray-600 truncate max-w-md" title={file.subject}>
                          {file.subject}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                            {file.currentDeskName?.charAt(0) || 'U'}
                        </div>
                        <span className="text-gray-700">{file.currentDeskName || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDateTime(file.archivedAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/efile/${file.id}`}>
                        <Button variant="outline" size="sm" className="gap-1 text-indigo-600 border-indigo-200 hover:bg-indigo-50">
                          <Eye className="h-3.5 w-3.5" /> View
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {files.length} of {totalFiles} files
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === 1} 
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === totalPages} 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordRoom;
